/**
 * SurveyResultsContent Component
 * Renders aggregated survey results grouped by section.
 * Scale questions render as a grid of compact bar chart cards; text questions
 * render as accordions that start expanded (MED-1345).
 */

import { useState, useEffect } from 'react';
import { Button, Center, Loader, Text, Stack } from '@mantine/core';
import { BarChart2, Lock } from 'react-feather';
import { StackedBarChart, ScaleLegend } from '../../../../components/common/StackedBarChart';
import { TextResponseAccordion } from '../../../../components/common/TextResponseAccordion';
import {
  feedbackSurveysApi,
  type AggregatedSurveyResults,
  type SurveyQuestion,
} from '../../../../services/api/feedbackSurveys';

import styles from './SurveyResultsModal.module.css';

interface SurveyResultsContentProps {
  cohortId: string;
  formType: string;
  groupId?: string;
  sessionId?: string;
}

export function SurveyResultsContent({ cohortId, formType, groupId, sessionId }: SurveyResultsContentProps) {
  const [results, setResults] = useState<AggregatedSurveyResults | null>(null);
  const [loading, setLoading] = useState(true);
  // A failed load must not read as "no responses" (MED-1345 design review).
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    feedbackSurveysApi
      .getAggregatedResults(cohortId, formType, groupId, sessionId)
      .then((data) => {
        if (!cancelled) {
          setResults(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResults(null);
          setLoadFailed(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cohortId, formType, groupId, sessionId, reloadKey]);

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (loadFailed) {
    return (
      <div className={styles.emptyState}>
        <Text size="sm" c="#111">
          We could not load these results.
        </Text>
        <Button
          size="xs"
          variant="light"
          mt="sm"
          onClick={() => setReloadKey((k) => k + 1)}
        >
          Try again
        </Button>
      </div>
    );
  }

  if (!results || results.total_submissions === 0) {
    return (
      <div className={styles.emptyState}>
        <BarChart2 size={32} color="var(--mantine-color-dimmed)" />
        <Text size="sm" c="#111" mt="sm">
          No responses yet. Results appear here as participants submit the survey.
        </Text>
      </div>
    );
  }

  const hasScaleQuestions = results.sections.some((section) =>
    section.questions.some((q) => q.question_category === 'scale'),
  );
  // A form without headings arrives as one "General" section; a title adds nothing there.
  const showSectionTitles =
    results.sections.length > 1 || results.sections[0]?.section_name !== 'General';

  return (
    <Stack gap="md">
      <div className={styles.resultsHeader}>
        <Text size="md" fw={600}>
          Results
        </Text>
        <span className={styles.respondentCount}>
          {results.total_submissions} of {results.total_participants} participants responded
        </span>
      </div>

      {/* MED-1345: the API sends no names to any role, so this always holds. */}
      <div className={styles.privacyNote}>
        <Lock size={14} color="#28463A" />
        Responses are anonymous. Participants were told their answers are shared without their
        names.
      </div>

      {hasScaleQuestions && <ScaleLegend note="% = agree or satisfied" />}

      {results.sections.map((section) => {
        const scaleQuestions = section.questions.filter(
          (q: SurveyQuestion) => q.question_category === 'scale',
        );
        const textQuestions = section.questions.filter(
          (q: SurveyQuestion) => q.question_category === 'text',
        );

        return (
          <section key={section.section_name} className={styles.section}>
            {showSectionTitles && (
              <div className={styles.sectionHead}>
                <h4 className={styles.sectionTitle}>{section.section_name}</h4>
                <span className={styles.sectionMeta}>
                  {section.questions.length}{' '}
                  {section.questions.length === 1 ? 'question' : 'questions'}
                </span>
              </div>
            )}

            {scaleQuestions.length > 0 && (
              <div className={styles.questionGrid}>
                {scaleQuestions.map((question: SurveyQuestion) => (
                  <StackedBarChart
                    key={question.question_id}
                    questionText={question.question_text}
                    responses={question.responses}
                    totalResponses={question.total_responses}
                    satisfactionPercentage={question.satisfaction_percentage}
                  />
                ))}
              </div>
            )}

            {textQuestions.length > 0 && (
              <div style={{ marginTop: scaleQuestions.length > 0 ? 20 : 0 }}>
                {scaleQuestions.length > 0 && (
                  <div className={styles.sectionHead}>
                    <h4 className={styles.sectionTitle}>In their words</h4>
                  </div>
                )}
                {textQuestions.map((question: SurveyQuestion) => (
                  <TextResponseAccordion
                    key={question.question_id}
                    questionText={question.question_text}
                    responses={question.text_responses ?? []}
                    defaultExpanded
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </Stack>
  );
}

export default SurveyResultsContent;
